import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { LocalStorageApiService } from '@nebalkon-workspace/browser-api';
import { AuthService, STAFF_ROLES } from '@nebalkon-workspace/auth';
import { Capacitor } from '@capacitor/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import {
  ImageEncoder,
  LabelType,
  NiimbotCapacitorBleClient,
} from '@mmote/niimbluelib';
import { renderNameBadgeCanvas } from './name-badge-canvas.util';
import { renderRotatedQrLabelCanvas } from './qr-label-canvas.util';
import type { NameBadgeText } from './name-badge-text.interface';
import type { QrLabelText } from './qr-label-text.interface';

/** Keys for Niimbot printer settings in mobile WebView `localStorage` (stable strings). */
export const NIIMBOT_LOCAL_STORAGE_KEYS = {
  qrLabelPresetId: 'printerQrLabelPresetId',
  labelsPerBatch: 'printerLabelsPerBatch',
} as const;

/** Niimbot primary BLE service (see NIIMBOT community wiki). */
export const NIIMBOT_BLE_SERVICE_UUID = 'e7810a71-73ae-499d-8c15-faa9aef0c3f2';

const NIIMBOT_SERVICE_COMPACT = 'e7810a7173ae499d8c15faa9aef0c3f2';

function normalizeUuidForCompare(uuid: string): string {
  return uuid.replace(/-/g, '').toLowerCase();
}

/**
 * Niimbot printers often do **not** put the main service UUID in the advertisement.
 * Filtering `requestLEScan` by `services` then yields zero devices. We scan broadly
 * and keep devices that look like Niimbot by name or advertised UUIDs.
 */
function isLikelyNiimbotAdvertisement(result: {
  device: { name?: string; uuids?: string[] };
  localName?: string;
  uuids?: string[];
}): boolean {
  const name = (result.device.name ?? result.localName ?? '').trim();
  if (name.length > 0) {
    if (
      /^(B1|B18|B21|B11|B16|B2|D11|D110|D101|NIIM)/i.test(name) ||
      /NIIMBOT|NIIM/i.test(name)
    ) {
      return true;
    }
  }

  const allUuids = [...(result.uuids ?? []), ...(result.device.uuids ?? [])];
  for (const u of allUuids) {
    const c = normalizeUuidForCompare(u);
    if (
      c.includes('e7810a71') ||
      c === NIIMBOT_SERVICE_COMPACT ||
      c.includes(NIIMBOT_SERVICE_COMPACT)
    ) {
      return true;
    }
  }

  return false;
}

export interface NiimbotScannedDevice {
  deviceId: string;
  name: string;
}

export interface LabelSizePreset {
  id: string;
  widthMm: number;
  heightMm: number;
  widthPx: number;
  heightPx: number;
}

function mmToPx(mm: number): number {
  return Math.round((mm * 203) / 25.4);
}

function capPrintWidth(widthPx: number): number {
  /** B1 printhead is 384 px (NiimBlueLib printer_models). */
  return Math.min(384, widthPx);
}

/** Presets for label dimensions; QR is centered on the label canvas. */
export const LABEL_SIZE_PRESETS: LabelSizePreset[] = [
  {
    id: '40x30',
    widthMm: 40,
    heightMm: 30,
    widthPx: capPrintWidth(mmToPx(40)),
    heightPx: mmToPx(30),
  },
  {
    id: '40x40',
    widthMm: 40,
    heightMm: 40,
    widthPx: capPrintWidth(mmToPx(40)),
    heightPx: mmToPx(40),
  },
  {
    id: '40x60',
    widthMm: 40,
    heightMm: 60,
    widthPx: capPrintWidth(mmToPx(40)),
    heightPx: mmToPx(60),
  },
  {
    id: '50x30',
    widthMm: 50,
    heightMm: 30,
    widthPx: capPrintWidth(mmToPx(50)),
    heightPx: mmToPx(30),
  },
  {
    id: '50x40',
    widthMm: 50,
    heightMm: 40,
    widthPx: capPrintWidth(mmToPx(50)),
    heightPx: mmToPx(40),
  },
  {
    id: '50x50',
    widthMm: 50,
    heightMm: 50,
    widthPx: capPrintWidth(mmToPx(50)),
    heightPx: mmToPx(50),
  },
];

/**
 * Fallback label preset when nothing valid is stored under
 * {@link NIIMBOT_LOCAL_STORAGE_KEYS.qrLabelPresetId}.
 */
export const DEFAULT_LABEL_PRESET_ID = '40x60';

/** Chest name badges: wide label so long positions fit on one line. */
export const DEFAULT_NAME_BADGE_PRESET_ID = '50x30';

/** Fixed payload encoded in staff QR labels (not user-configurable). */
const DEFAULT_QR_PRINT_PAYLOAD = 'https://nebalkon.by/box/id';

/** Default number of identical labels per print job when nothing valid is stored. */
export const DEFAULT_LABELS_PER_BATCH = 4;

const MIN_LABELS_PER_BATCH = 1;
const MAX_LABELS_PER_BATCH = 99;

/** BLE on Android is slower to respond while the printhead is active. */
const ANDROID_PRINT_STATUS_TIMEOUT_MS = 20_000;
const ANDROID_PRINT_PAGE_TIMEOUT_MS = 30_000;
const DEFAULT_PRINT_STATUS_TIMEOUT_MS = 10_000;
const DEFAULT_PRINT_PAGE_TIMEOUT_MS = 15_000;
const DEFAULT_PRINT_STATUS_POLL_INTERVAL_MS = 500;

@Injectable({ providedIn: 'root' })
export class NiimbotPrinterService {
  readonly #auth = inject(AuthService);
  readonly #storage = inject(LocalStorageApiService);

  /**
   * Fixed QR payload for the printer screen preview and print when no custom text is passed.
   * For ad-hoc prints from other features, pass the string into {@link printQrCode} /
   * {@link printQrCodeWithSavedLabelPreset}.
   */
  readonly qrPrintPayload = DEFAULT_QR_PRINT_PAYLOAD;

  /**
   * Niimbot printer is allowed only for authenticated users with a staff role
   * (same set as {@link STAFF_ROLES} / dashboard access).
   */
  readonly mayUseNiimbotPrinter = computed(() => {
    if (!this.#auth.isAuthenticated()) {
      return false;
    }
    const roles = this.#auth.roles();
    return STAFF_ROLES.some((r) => roles.includes(r));
  });

  readonly discoveredDevices = signal<readonly NiimbotScannedDevice[]>([]);
  readonly isScanning = signal(false);
  readonly isConnecting = signal(false);
  readonly isPrinting = signal(false);
  /**
   * Current BLE connection (session only, not persisted). Reconnect after app restart if needed.
   */
  readonly connectedDevice = signal<{
    deviceId: string;
    name: string;
  } | null>(null);

  readonly defaultLabelPresetId = DEFAULT_LABEL_PRESET_ID;

  /**
   * Label preset id persisted in `localStorage` for QR printing; falls back to {@link DEFAULT_LABEL_PRESET_ID}.
   */
  getSavedQrLabelPresetId(): string {
    const raw = this.#storage.getItem(
      NIIMBOT_LOCAL_STORAGE_KEYS.qrLabelPresetId,
    );
    if (raw && LABEL_SIZE_PRESETS.some((p) => p.id === raw)) {
      return raw;
    }
    return DEFAULT_LABEL_PRESET_ID;
  }

  /** Resolved preset for QR prints using saved label size. */
  getLabelPresetForQrPrint(): LabelSizePreset {
    return (
      this.getLabelPreset(this.getSavedQrLabelPresetId()) ??
      LABEL_SIZE_PRESETS[0]
    );
  }

  saveQrLabelPresetId(presetId: string): void {
    const id = LABEL_SIZE_PRESETS.some((p) => p.id === presetId)
      ? presetId
      : DEFAULT_LABEL_PRESET_ID;
    this.#storage.setItem(NIIMBOT_LOCAL_STORAGE_KEYS.qrLabelPresetId, id);
  }

  /**
   * Copies per print job persisted in `localStorage`; falls back to {@link DEFAULT_LABELS_PER_BATCH}.
   */
  getSavedLabelsPerBatch(): number {
    const raw = this.#storage.getItem(
      NIIMBOT_LOCAL_STORAGE_KEYS.labelsPerBatch,
    );
    if (raw == null || raw.trim() === '') {
      return DEFAULT_LABELS_PER_BATCH;
    }
    const n = parseInt(raw.trim(), 10);
    if (!Number.isFinite(n)) {
      return DEFAULT_LABELS_PER_BATCH;
    }
    return Math.min(MAX_LABELS_PER_BATCH, Math.max(MIN_LABELS_PER_BATCH, n));
  }

  saveLabelsPerBatch(raw: number): void {
    const n = Number.isFinite(raw) ? Math.floor(raw) : DEFAULT_LABELS_PER_BATCH;
    const clamped = Math.min(
      MAX_LABELS_PER_BATCH,
      Math.max(MIN_LABELS_PER_BATCH, n),
    );
    this.#storage.setItem(
      NIIMBOT_LOCAL_STORAGE_KEYS.labelsPerBatch,
      String(clamped),
    );
  }

  /**
   * Print a QR code using the label dimensions saved on the printer screen.
   * @param copies — omit to use {@link getSavedLabelsPerBatch}.
   * @param label — optional type line (top) and caption (below QR).
   */
  async printQrCodeWithSavedLabelPreset(
    text: string,
    copies?: number,
    label?: QrLabelText,
  ): Promise<void> {
    return this.printQrCode(
      text,
      this.getLabelPresetForQrPrint(),
      copies,
      label,
    );
  }

  #client: NiimbotCapacitorBleClient | null = null;
  #scanDeviceMap = new Map<string, NiimbotScannedDevice>();

  constructor() {
    effect(() => {
      if (!this.mayUseNiimbotPrinter()) {
        void this.disconnect();
      }
    });
  }

  #assertStaffPrinterAccess(): void {
    if (!this.mayUseNiimbotPrinter()) {
      throw new Error('STAFF_PRINTER_ACCESS_DENIED');
    }
  }

  isBlePlatform(): boolean {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android';
  }

  getLabelPreset(id: string): LabelSizePreset | undefined {
    return LABEL_SIZE_PRESETS.find((p) => p.id === id);
  }

  async startScan(): Promise<void> {
    this.#assertStaffPrinterAccess();
    if (!this.isBlePlatform()) {
      throw new Error('BLE_UNAVAILABLE');
    }
    await this.stopScan();
    this.#scanDeviceMap.clear();
    this.discoveredDevices.set([]);

    await BleClient.initialize({ androidNeverForLocation: true });
    const enabled = await BleClient.isEnabled();
    if (!enabled) {
      throw new Error('BLUETOOTH_DISABLED');
    }

    this.isScanning.set(true);

    try {
      if (Capacitor.getPlatform() === 'android') {
        try {
          const bonded = await BleClient.getBondedDevices();
          for (const d of bonded) {
            const label = (d.name ?? '').trim();
            if (
              label.length > 0 &&
              isLikelyNiimbotAdvertisement({
                device: d,
                localName: d.name,
              })
            ) {
              this.#scanDeviceMap.set(d.deviceId, {
                deviceId: d.deviceId,
                name: label,
              });
            }
          }
          this.discoveredDevices.set([...this.#scanDeviceMap.values()]);
        } catch {
          /* ignore bonded enumeration errors */
        }
      }

      /**
       * Empty options = no service filter. Required: many Niimbots do not advertise
       * `e7810a71-...` in the BLE advertisement, so a service-only scan never fires.
       */
      await BleClient.requestLEScan({}, (result) => {
        if (!isLikelyNiimbotAdvertisement(result)) {
          return;
        }
        const d = result.device;
        const name =
          (d.name && d.name.length > 0 ? d.name : undefined) ??
          (result.localName && result.localName.length > 0
            ? result.localName
            : undefined) ??
          d.deviceId;
        this.#scanDeviceMap.set(d.deviceId, {
          deviceId: d.deviceId,
          name,
        });
        this.discoveredDevices.set([...this.#scanDeviceMap.values()]);
      });
    } catch (e) {
      this.isScanning.set(false);
      throw e;
    }
  }

  async stopScan(): Promise<void> {
    if (!this.isScanning()) {
      return;
    }
    try {
      await BleClient.stopLEScan();
    } finally {
      this.isScanning.set(false);
    }
  }

  async connect(deviceId: string, displayName?: string): Promise<void> {
    this.#assertStaffPrinterAccess();
    if (!this.isBlePlatform()) {
      throw new Error('BLE_UNAVAILABLE');
    }
    this.isConnecting.set(true);
    try {
      await this.stopScan();
      await this.#disconnectClientOnly();

      const client = new NiimbotCapacitorBleClient();
      const info = await client.connect({ deviceId });
      this.#client = client;

      const name =
        displayName ??
        info.deviceName ??
        this.#scanDeviceMap.get(deviceId)?.name ??
        deviceId;

      this.connectedDevice.set({ deviceId, name });
    } finally {
      this.isConnecting.set(false);
    }
  }

  async disconnect(): Promise<void> {
    await this.stopScan();
    await this.#disconnectClientOnly();
    this.connectedDevice.set(null);
  }

  async #disconnectClientOnly(): Promise<void> {
    if (this.#client) {
      try {
        await this.#client.disconnect();
      } finally {
        this.#client = null;
      }
    }
  }

  isConnected(): boolean {
    return this.#client?.isConnected() ?? false;
  }

  getNameBadgeLabelPreset(): LabelSizePreset {
    return (
      this.getLabelPreset(DEFAULT_NAME_BADGE_PRESET_ID) ?? LABEL_SIZE_PRESETS[0]
    );
  }

  async printNameBadge(
    badge: NameBadgeText,
    copies?: number,
    preset?: LabelSizePreset,
  ): Promise<void> {
    const canvas = document.createElement('canvas');
    const resolvedPreset = preset ?? this.getNameBadgeLabelPreset();
    canvas.width = resolvedPreset.widthPx;
    canvas.height = resolvedPreset.heightPx;
    renderNameBadgeCanvas(canvas, badge);
    await this.#printCanvas(canvas, copies);
  }

  /**
   * @param copies — how many identical labels to print; omit to use {@link getSavedLabelsPerBatch}.
   * @param label — optional type line (top) and caption (below QR).
   */
  async printQrCode(
    text: string,
    preset: LabelSizePreset,
    copies?: number,
    label?: QrLabelText,
  ): Promise<void> {
    const canvas = document.createElement('canvas');
    canvas.width = preset.widthPx;
    canvas.height = preset.heightPx;
    await renderRotatedQrLabelCanvas(canvas, text, label);
    await this.#printCanvas(canvas, copies);
  }

  async #printCanvas(canvas: HTMLCanvasElement, copies?: number): Promise<void> {
    this.#assertStaffPrinterAccess();
    const client = this.#client;
    if (!client?.isConnected()) {
      throw new Error('NOT_CONNECTED');
    }

    const totalPages =
      copies === undefined
        ? this.getSavedLabelsPerBatch()
        : Math.min(
            MAX_LABELS_PER_BATCH,
            Math.max(MIN_LABELS_PER_BATCH, Math.floor(copies)),
          );

    this.isPrinting.set(true);
    try {
      const encoded = ImageEncoder.encodeCanvas(canvas, 'left');
      const printTaskType = client.getPrintTaskType() ?? 'B1';
      const isAndroid = Capacitor.getPlatform() === 'android';
      const printTask = client.abstraction.newPrintTask(printTaskType, {
        totalPages,
        labelType: LabelType.WithGaps,
        density: 3,
        statusTimeoutMs: isAndroid
          ? ANDROID_PRINT_STATUS_TIMEOUT_MS
          : DEFAULT_PRINT_STATUS_TIMEOUT_MS,
        pageTimeoutMs: isAndroid
          ? ANDROID_PRINT_PAGE_TIMEOUT_MS
          : DEFAULT_PRINT_PAGE_TIMEOUT_MS,
        statusPollIntervalMs: DEFAULT_PRINT_STATUS_POLL_INTERVAL_MS,
      });
      try {
        await printTask.printInit();
        await printTask.printPage(encoded, totalPages);
        await printTask.waitForPageFinished();
        await printTask.waitForFinished();
      } finally {
        try {
          await client.abstraction.printEnd();
        } catch (cleanupError) {
          // printEnd can time out on Android after a successful job; do not fail the user flow.
          console.warn('Niimbot printEnd cleanup failed:', cleanupError);
        }
      }
    } finally {
      this.isPrinting.set(false);
    }
  }
}
